import { ref } from "vue";
import { useMutation, useQueryClient } from "@tanstack/vue-query";
import { useToast } from "vue-toastification";
import {
  ReconciliarPendientesIzipayPorLotesAction,
  type ReconciliacionIzipayLoteProgreso,
  type ReconciliacionPendientesIzipayAcumulada,
} from "../actions";
import { invalidateDashboardQueries } from "@/utils/helper/invalidateDashboard";

const toast = useToast();

function toastDesdeResultadoLotes(d: ReconciliacionPendientesIzipayAcumulada) {
  const partes = [
    d.lotesEjecutados > 1
      ? `${d.lotesEjecutados} lote(s) · ${d.consultadosTotal} consulta(s) en Izipay`
      : `${d.consultadosTotal} pedido(s) consultado(s) en Izipay`,
    `${d.marcadoPagado} pasaron a pagado`,
    d.cancelados > 0 ? `${d.cancelados} cancelado(s)` : null,
    d.errores > 0 ? `${d.errores} error(es)` : null,
  ].filter(Boolean);
  toast.success(partes.join(" · "));
}

/**
 * Sincroniza pedidos pendientes con Izipay en lotes (30 por defecto) hasta agotar candidatos
 * o hasta un lote sin avances a Pagado/Cancelado.
 */
export function useReconciliarPendientesIzipay() {
  const queryClient = useQueryClient();
  const progresoLotesIzipay = ref<ReconciliacionIzipayLoteProgreso | null>(null);

  const mutation = useMutation({
    mutationFn: async (limitPorLote?: number) => {
      progresoLotesIzipay.value = null;
      const lim = limitPorLote ?? 30;
      return ReconciliarPendientesIzipayPorLotesAction(lim, (p) => {
        progresoLotesIzipay.value = p;
      });
    },
    onSuccess: async (res) => {
      progresoLotesIzipay.value = null;
      const d = res.data;
      if (!d) {
        toast.success("Sincronización completada.");
      } else {
        toastDesdeResultadoLotes(d);
      }
      await queryClient.invalidateQueries({ queryKey: ["gpedListPedidos"] });
      await queryClient.invalidateQueries({ queryKey: ["gepPedidoDetalles"] });
      await invalidateDashboardQueries(queryClient);
      await queryClient.refetchQueries({ queryKey: ["gpedListPedidos"] });
    },
    onError: (err: unknown) => {
      progresoLotesIzipay.value = null;
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      const msg =
        e?.response?.data?.message || e?.message || "No se pudo sincronizar con Izipay";
      toast.error(typeof msg === "string" ? msg : "Error al sincronizar con Izipay");
    },
  });

  return { ...mutation, progresoLotesIzipay };
}
